import React, { useEffect, useRef } from 'react';

// ─── Date Input (handles slash logic) ────────────────────────────────────────

function DateTextInput({
  inputId,
  inputRef,
  value,
  onChange,
  onCommit,
  onAdvanceToTime,
  onKeyDown,
  placeholder,
  isDisabled,
  isInvalid,
  isRequired,
}: {
  inputId: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  onCommit: () => void;
  onAdvanceToTime?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
}) {
  const rafRef = useRef<number>();
  useEffect(() => () => cancelAnimationFrame(rafRef.current!), []);

  const raf = (fn: () => void) => {
    rafRef.current = requestAnimationFrame(fn);
  };

  // Check if cursor is at the end of a complete year segment (4 digits after last slash)
  const isAtYearEnd = (input: HTMLInputElement, pos?: number) => {
    const p = pos ?? input.selectionStart ?? 0;
    const lastSlash = input.value.lastIndexOf('/');
    if (lastSlash === -1) return false;
    const yearPart = input.value.slice(lastSlash + 1);
    return yearPart.length >= 4 && p >= input.value.length;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const pos = input.selectionStart ?? 0;
    const val = input.value;

    // Tab: always commit, then advance to time input if present
    if (e.key === 'Tab' && !e.shiftKey) {
      onCommit();

      if (onAdvanceToTime) {
        e.preventDefault();
        onAdvanceToTime();
      }

      return;
    }

    if (e.key === 'Enter') {
      onCommit();
    }

    // Space or ArrowRight at end of complete year → advance to time
    if ((e.key === ' ' || e.key === 'ArrowRight') && onAdvanceToTime && pos === val.length && isAtYearEnd(input)) {
      e.preventDefault();
      onAdvanceToTime();
      return;
    }

    if (e.key === '/') {
      // Skip over an existing slash
      if (val[pos] === '/') {
        e.preventDefault();
        input.setSelectionRange(pos + 1, pos + 1);
        return;
      }

      // Block if it would create a double slash
      if (pos === 0 || val[pos - 1] === '/') {
        e.preventDefault();
        return;
      }
    }

    // Auto-insert slash when a 2-char segment is full, cap year at 4
    if (/^\d$/.test(e.key)) {
      const lastSlash = val.lastIndexOf('/', pos - 1);
      const segmentLen = pos - (lastSlash + 1);
      const slashCount = (val.match(/\//g) || []).length;

      // In the year segment — cap at 4 digits
      if (slashCount >= 2) {
        const yearStart = val.lastIndexOf('/') + 1;
        const yearLen = val.length - yearStart;
        if (yearLen >= 4 && pos >= yearStart) {
          if (onAdvanceToTime) {
            e.preventDefault();
            onAdvanceToTime();
          } else {
            e.preventDefault();
          }
          return;
        }

        // After typing this digit, if year will be 4 chars, advance to time
        if (yearLen === 3 && pos >= yearStart && onAdvanceToTime) {
          raf(() => onAdvanceToTime());
        }
        return;
      }

      if (segmentLen >= 2 && slashCount < 2) {
        e.preventDefault();
        const newVal = val.slice(0, pos) + '/' + e.key + val.slice(pos);
        onChange(newVal);
        raf(() => {
          input.setSelectionRange(pos + 2, pos + 2);
        });
        return;
      }

      if (segmentLen === 1 && val[pos] === '/') {
        raf(() => {
          input.setSelectionRange(pos + 2, pos + 2);
        });
      }
    }

    onKeyDown?.(e);
  };

  return (
    <input
      id={inputId}
      ref={inputRef}
      type="text"
      className="min-w-0 w-[11ch] bg-transparent outline-none tabular-nums placeholder:text-neutral-alpha-7"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^\d/]/g, '');
        onChange(cleaned);
      }}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      aria-invalid={isInvalid || undefined}
      aria-required={isRequired || undefined}
    />
  );
}

export default DateTextInput;
